import Highcharts from 'highcharts';

/**
 * Chart configuration utility class
 * Provides consistent chart styling and configuration across all simulation result pages
 * Based on CubeTrends chart styling patterns
 */

/**
 * Base chart plot options with consistent styling
 */
export const BASE_PLOT_OPTIONS: Highcharts.PlotOptions = {
  scatter: {
    marker: {
      radius: 5,
      states: {
        hover: {
          enabled: true,
          lineColor: 'rgb(100,100,100)',
        },
      },
    },
  },
  line: {
    marker: {
      radius: 5,
      enabled: true,
      states: {
        hover: {
          enabled: true,
          lineColor: 'rgb(100,100,100)',
        },
      },
    },
    states: {
      hover: {
        lineWidth: 2,
      },
    },
    enableMouseTracking: true,
  },
  series: {
    lineWidth: 2,
  },
};

/**
 * Base exporting configuration
 */
export const BASE_EXPORTING_CONFIG: Highcharts.ExportingOptions = {
  enabled: true,
  fallbackToExportServer: false,
  buttons: {
    contextButton: {
      menuItems: [
        'viewFullscreen',
        'printChart',
        'separator',
        'downloadPNG',
        'downloadJPEG',
        'downloadPDF',
        'downloadSVG',
      ],
    },
  },
};

/**
 * Marker configuration interface
 */
export interface MarkerConfig {
  symbol?: string;
  radius?: number;
  fillColor?: string;
  lineWidth?: number;
  lineColor?: string;
  enabled?: boolean;
}

/**
 * Series configuration interface
 */
export interface SeriesConfig {
  type: 'line' | 'scatter';
  name: string;
  data: number[][];
  color: string;
  marker?: MarkerConfig;
  lineWidth?: number;
  dashStyle?: Highcharts.DashStyleValue;
  custom?: Record<string, any>;
}

/**
 * Creates a flat marker configuration (no gradient, consistent with CubeTrends)
 */
export function createFlatMarker(
  symbol: string = 'circle',
  color: string,
  radius: number = 5,
): MarkerConfig {
  return {
    symbol,
    radius,
    fillColor: color,
    lineWidth: 0, // No border for truly flat appearance
    lineColor: color, // Match line color to fill color
    enabled: true,
  };
}

/**
 * Creates a line series marker configuration
 */
export function createLineMarker(
  symbol: string = 'circle',
  color: string,
  radius: number = 5,
  enabled: boolean = true,
): MarkerConfig {
  return {
    symbol,
    radius,
    fillColor: color,
    enabled,
  };
}

/**
 * Creates a scatter series with flat markers
 */
export function createScatterSeries(config: {
  name: string;
  data: number[][];
  color: string;
  symbol?: string;
  custom?: Record<string, any>;
}): Highcharts.SeriesOptionsType {
  return {
    type: 'scatter',
    name: config.name,
    data: config.data,
    color: config.color,
    lineWidth: 0,
    marker: {
      symbol: config.symbol ?? 'circle',
      fillColor: config.color,
    },
    legendSymbol: 'lineMarker',
    custom: config.custom,
  } as Highcharts.SeriesOptionsType;
}

/**
 * Creates a line series with consistent styling
 */
export function createLineSeries(config: {
  name: string;
  data: number[][];
  color: string;
  symbol?: string;
  markerEnabled?: boolean;
  dashStyle?: Highcharts.DashStyleValue;
  lineWidth?: number;
  custom?: Record<string, any>;
}): Highcharts.SeriesOptionsType {
  return {
    type: 'line',
    name: config.name,
    data: config.data,
    color: config.color,
    lineWidth: config.lineWidth ?? 2,
    dashStyle: config.dashStyle ?? 'Solid',
    marker: createLineMarker(
      config.symbol ?? 'circle',
      config.color,
      5,
      config.markerEnabled ?? true,
    ),
    legendSymbol: 'lineMarker',
    custom: config.custom,
  } as Highcharts.SeriesOptionsType;
}

/**
 * Creates a trend line series (typically dashed, no markers)
 */
export function createTrendLineSeries(config: {
  name: string;
  data: number[][];
  color: string;
  custom?: Record<string, any>;
}): Highcharts.SeriesOptionsType {
  return {
    type: 'line',
    name: config.name,
    data: config.data,
    color: config.color,
    lineWidth: 2,
    dashStyle: 'Dash',
    marker: {
      enabled: false,
    },
    legendSymbol: 'lineMarker',
    custom: config.custom,
  } as Highcharts.SeriesOptionsType;
}

/**
 * Base chart options for linear scale charts
 */
export function createBaseLinearChartOptions(config: {
  title: string;
  xAxisTitle: string;
  yAxisTitle: string;
  series: Highcharts.SeriesOptionsType[];
  xMin?: number;
  xMax?: number;
  yMin?: number;
  yMax?: number;
  tooltipFormatter?: Highcharts.TooltipFormatterCallbackFunction;
}): Highcharts.Options {
  return {
    chart: {
      backgroundColor: '#ffffff',
    },
    title: {
      text: config.title,
      style: {
        fontSize: '14px',
        fontWeight: '600',
      },
    },
    accessibility: {
      enabled: true,
      description: `This chart shows ${config.title}`,
    },
    credits: { enabled: false },
    xAxis: {
      type: 'linear',
      title: { text: config.xAxisTitle },
      min: config.xMin,
      max: config.xMax,
      gridLineWidth: 1,
    },
    yAxis: {
      type: 'linear',
      title: { text: config.yAxisTitle },
      min: config.yMin,
      max: config.yMax,
    },
    legend: {
      enabled: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
    },
    tooltip: config.tooltipFormatter
      ? { formatter: config.tooltipFormatter }
      : {
          headerFormat: '<b>{series.name}</b><br />',
          pointFormat: '<b>X:</b> {point.x:.6f}<br/><b>Y:</b> {point.y:.6f}',
        },
    plotOptions: BASE_PLOT_OPTIONS,
    series: config.series,
    exporting: BASE_EXPORTING_CONFIG,
  };
}

/**
 * Base chart options for logarithmic scale charts
 */
export function createBaseLogChartOptions(config: {
  title: string;
  xAxisTitle: string;
  yAxisTitle: string;
  series: Highcharts.SeriesOptionsType[];
  xAxisType?: 'linear' | 'logarithmic';
  yAxisType?: 'linear' | 'logarithmic';
  xMin?: number;
  xMax?: number;
  yMin?: number;
  yMax?: number;
  xTickInterval?: number;
  xMinorTickInterval?: number;
  tooltipFormatter?: Highcharts.TooltipFormatterCallbackFunction;
}): Highcharts.Options {
  return {
    chart: {
      backgroundColor: '#ffffff',
    },
    title: {
      text: config.title,
      style: {
        fontSize: '14px',
        fontWeight: '600',
      },
    },
    accessibility: {
      enabled: true,
      description: `This chart shows ${config.title}`,
    },
    credits: { enabled: false },
    xAxis: {
      type: config.xAxisType ?? 'logarithmic',
      title: { text: config.xAxisTitle },
      min: config.xMin,
      max: config.xMax,
      tickInterval: config.xTickInterval,
      minorTickInterval: config.xMinorTickInterval,
      gridLineWidth: 1,
    },
    yAxis: {
      type: config.yAxisType ?? 'logarithmic',
      title: { text: config.yAxisTitle },
      min: config.yMin,
      max: config.yMax,
    },
    legend: {
      enabled: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
    },
    tooltip: config.tooltipFormatter
      ? { formatter: config.tooltipFormatter }
      : {
          headerFormat: '<b>{series.name}</b><br />',
          pointFormat: '<b>X:</b> {point.x:.6f}<br/><b>Y:</b> {point.y:.8f}',
        },
    plotOptions: BASE_PLOT_OPTIONS,
    series: config.series,
    exporting: BASE_EXPORTING_CONFIG,
  };
}

/**
 * Common marker symbols
 */
export const MARKER_SYMBOLS = {
  CIRCLE: 'circle',
  TRIANGLE: 'triangle',
  SQUARE: 'square',
  DIAMOND: 'diamond',
} as const;

/**
 * Helper function to update chart series dynamically
 * Removes series not in newSeries and adds new ones while preserving order
 */
export function updateChartSeries(
  chart: Highcharts.Chart,
  newSeries: Highcharts.SeriesOptionsType[],
): void {
  const newSeriesNames = new Set(newSeries.map((s) => s.name));

  // Remove series that are no longer needed
  for (let i = chart.series.length - 1; i >= 0; i--) {
    const series = chart.series[i];
    if (!newSeriesNames.has(series.name)) {
      series.remove(false);
    }
  }

  // Add new series that don't exist yet
  const existingSeriesNames = new Set(chart.series.map((s) => s.name));
  newSeries.forEach((s) => {
    if (!existingSeriesNames.has(s.name ?? '')) {
      chart.addSeries(s, false);
    }
  });

  // Redraw the chart once
  chart.redraw();
}

/**
 * Updates chart series visibility without flickering
 * Uses setVisible() and showInLegend to toggle series visibility smoothly
 * All series should already be added to the chart before calling this function
 * This provides a flicker-free experience when toggling series on/off
 */
export function updateChartSeriesVisibilitySmooth(
  chart: Highcharts.Chart,
  selectedSeriesNames: Set<string>,
): void {
  if (!chart) return;

  chart.series.forEach((series) => {
    const isSelected = selectedSeriesNames.has(series.name ?? '');
    series.setVisible(isSelected, false);
    // Also hide from legend when not selected
    series.update({ showInLegend: isSelected } as Highcharts.SeriesOptionsType, false);
  });

  chart.redraw();
}

/**
 * Updates multiple charts' series visibility without flickering
 * Uses setVisible() and showInLegend to toggle series visibility smoothly
 * All series should already be added to the charts before calling this function
 * Useful for pages with multiple charts that should stay synchronized
 */
export function updateMultipleChartsSeriesVisibilitySmooth(
  charts: Highcharts.Chart[],
  selectedSeriesNames: Set<string>,
): void {
  charts.forEach((chart) => {
    if (!chart) return;

    chart.series.forEach((series) => {
      const isSelected = selectedSeriesNames.has(series.name ?? '');
      series.setVisible(isSelected, false);
      // Also hide from legend when not selected
      series.update({ showInLegend: isSelected } as Highcharts.SeriesOptionsType, false);
    });

    chart.redraw();
  });
}

